import { SparklesIcon, TruckIcon } from '@heroicons/react/24/outline'
import { useState } from 'react'
import formatXmlLib from 'xml-formatter'

import { ProfinderTransactionLog } from '@engine/models/admin'

import { ErrorState } from '#components/ErrorState'
import { ExpandableJsonPanel } from '#components/ExpandableJsonPanel'
import { LoadingState } from '#components/LoadingState'
import { usePagedFetch } from '#hooks/useAdminResource.js'
import { formatDateTime, formatDuration } from '#utils/format'

interface ProfinderTransactionsProps {
  trace: string
}

const getDurationColor = (duration?: number): string => {
  if (!duration) return 'text-base-content'
  if (duration < 100) return 'text-success'
  if (duration < 500) return 'text-info'
  if (duration < 1000) return 'text-warning'
  return 'text-error'
}

const isXml = (str: unknown): boolean => {
  if (typeof str !== 'string') return false
  const trimmed = str.trim()
  return trimmed.startsWith('<') && trimmed.endsWith('>')
}

const formatXml = (xml: string): string => {
  try {
    return formatXmlLib(xml, { indentation: '  ', collapseContent: true })
  } catch {
    return xml
  }
}

const formatContent = (content: { body?: string | object }): string => {
  if (typeof content.body === 'string') {
    if (isXml(content.body)) {
      return formatXml(content.body)
    }
  }

  return JSON.stringify(content, null, 2)
}

const TransactionRow = ({
  data,
  timestamp,
  message,
  isCarrier,
  tagColor,
}: {
  data?: any
  timestamp: string
  message: string
  isCarrier: boolean
  tagColor: string
}) => {
  const [isExpanded, setIsExpanded] = useState(false)

  return (
    <div className="rounded-box bg-neutral border border-base-300">
      <div
        className="p-4 cursor-pointer hover:bg-base-300/20 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 flex-1">
            <div
              className={`px-2 py-1 rounded text-xs font-semibold ${tagColor} flex items-center gap-1.5`}
            >
              {isCarrier ? (
                <TruckIcon className="w-3 h-3" />
              ) : (
                <SparklesIcon className="w-3 h-3" />
              )}
              {isCarrier ? 'CARRIER' : message?.toUpperCase() || 'EVENT'}
            </div>
            <span className="font-semibold">
              {isCarrier ? data?.id?.toUpperCase() : message}
            </span>
          </div>
          <div className="flex items-center gap-6 text-sm">
            <div className="text-base-content/70">
              <span className="font-medium">Time:</span>{' '}
              <span className="font-mono text-xs">
                {formatDateTime(timestamp)}
              </span>
            </div>
            <div
              className={`font-mono font-bold ${getDurationColor(data?.duration)}`}
            >
              {formatDuration(data?.duration)}
            </div>
            <svg
              className={`w-5 h-5 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>
        </div>
      </div>

      {isExpanded && (
        <div className="px-4 pb-4 space-y-4">
          {data?.request && (
            <div className="rounded-lg bg-blue-500/10 border border-blue-500/30 p-3">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-2 h-2 rounded-full bg-blue-400" />
                <h5 className="font-semibold text-blue-300 text-sm">Request</h5>
              </div>
              <ExpandableJsonPanel
                title=""
                data={data.request}
                maxHeight="max-h-64"
                preClassName="bg-slate-950/80 border border-blue-500/20 font-mono text-blue-100"
                formatter={formatContent}
                defaultExpanded={false}
              />
            </div>
          )}

          {data?.response && (
            <div className="rounded-lg bg-green-500/10 border border-green-500/30 p-3">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-2 h-2 rounded-full bg-green-400" />
                <h5 className="font-semibold text-green-300 text-sm">
                  Response
                </h5>
              </div>
              <ExpandableJsonPanel
                title=""
                data={data.response}
                maxHeight="max-h-64"
                preClassName="bg-slate-950/80 border border-green-500/20 font-mono text-green-100"
                formatter={formatContent}
                defaultExpanded={false}
              />
            </div>
          )}

          {!data?.request && !data?.response && (
            <div>
              <ExpandableJsonPanel
                title=""
                data={data}
                maxHeight="max-h-64"
                preClassName="bg-slate-950/80 border border-base-300 font-mono text-base-content"
                defaultExpanded={false}
              />
            </div>
          )}
        </div>
      )}
    </div>
  )
}

const ProfinderTransactions = ({ trace }: ProfinderTransactionsProps) => {
  const {
    items: transactions,
    isLoading,
    error,
  } = usePagedFetch<ProfinderTransactionLog>(
    'profinder-logs',
    trace ? { trace } : {},
    {
      enabled: !!trace,
    },
  )

  return (
    <div>
      <div className="flex items-center mb-6">
        <h3 className="text-xl font-bold">Transactions</h3>
      </div>

      {isLoading ? (
        <LoadingState />
      ) : error ? (
        <ErrorState error={error} onRetry={() => {}} />
      ) : transactions.length === 0 ? (
        <div className="rounded-box border border-warning/30 bg-warning/10 p-6 text-sm text-warning">
          No transactions found for this request.
        </div>
      ) : (
        <div className="space-y-2">
          {transactions
            .filter(
              t =>
                t.message !== 'profinder response' &&
                Object.keys(t.data ?? {}).length > 0,
            )
            .map(({ data, id, timestamp, message }) => {
              const isCarrier = data?.layer === 'carrier'
              const tagColor = isCarrier
                ? 'bg-blue-500/20 text-blue-300'
                : 'bg-purple-500/20 text-purple-300'

              return (
                <TransactionRow
                  key={id}
                  data={data}
                  timestamp={timestamp}
                  message={message}
                  isCarrier={isCarrier}
                  tagColor={tagColor}
                />
              )
            })}
        </div>
      )}
    </div>
  )
}

export { ProfinderTransactions }
